import { IconButton, Text, Flex, Tag } from '@chakra-ui/react';
import {
   Card,
   CardImage,
   CardContent,
   CardRight,
   CardActions,
   Icon,
   CardHeader,
} from '~/ui-components';
import { StageFrequencyEditor } from './StageFrequencyEditor';
import { StageStrictnessEditor } from './StageStrictnessEditor';
import { StageUseDatacaptureEditor } from './StageUseDatacaptureEditor';
import { faTrashCan } from '@fortawesome/pro-regular-svg-icons';
import { useLocalization } from '~/framework';
import { useStageEditor } from '../StageEditor';
import { Stage } from '../Stage';
import { useSiteInfo } from '~/pages/_shared';

type StageCardProps = {
   stage: Stage;
};

export function StageCard({ stage }: StageCardProps) {
   const { _js } = useLocalization();

   const { data: siteInfo } = useSiteInfo();
   const docSummaryInfo = stage.docSummaryInfo;
   const title = docSummaryInfo?.title;
   const image = docSummaryInfo?.standardImage;
   const releaseTitle = docSummaryInfo?.releaseTitle;
   const learningStandard = docSummaryInfo?.learningStandard;
   const quizzesWithOperatorView = siteInfo?.['dev-quizzes-with-operator-view'];
   const canIshowUseDatacaptureEditor =
      quizzesWithOperatorView && stage.doctype == 'guide' && docSummaryInfo?.workLogEnabled;

   const stageEditor = useStageEditor();

   function handleChange(newValues: Stage) {
      stageEditor.updateStage(stage, newValues);
   }

   return (
      <Card flexWrap="wrap" minHeight="112.5px">
         {image && <CardImage src={image} alt={title} />}
         <CardContent ml={image ? 4 : 8}>
            <CardHeader>{title}</CardHeader>
            {releaseTitle && <Text variant="muted">{_js('Version') + `: ${releaseTitle}`}</Text>}
            {learningStandard && <Text variant="muted">{learningStandard}</Text>}
            {!stage.docSummaryInfo?.public && (
               <Flex align="center" mt={3}>
                  <Tag size="sm" colorScheme="yellow">
                     {_js('Private Stage')}
                  </Tag>
                  <Text fontSize="sm" variant="muted" ml={2}>
                     {_js('Some users may not see this stage in their course')}
                  </Text>
               </Flex>
            )}
         </CardContent>
         <CardRight>
            <CardActions mr={4}>
               {canIshowUseDatacaptureEditor && (
                  <StageUseDatacaptureEditor stage={stage} onChange={handleChange} />
               )}
               <StageFrequencyEditor stage={stage} onChange={handleChange} />
               <StageStrictnessEditor stage={stage} onChange={handleChange} />
               <IconButton
                  aria-label={_js('Remove Stage')}
                  icon={<Icon type={faTrashCan} />}
                  onClick={() => stageEditor.removeStage(stage)}
                  _hover={{
                     backgroundColor: 'red.300',
                  }}
               />
            </CardActions>
         </CardRight>
      </Card>
   );
}
